import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, LinearProgress, Tooltip, Switch, FormControl, InputLabel,
  Select, MenuItem, Alert, Tabs, Tab
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
  ContentCopy as CopyIcon, Code as CodeIcon, Edit as EditIcon
} from '@mui/icons-material';
import { ApiContext } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';

const WEBHOOK_TYPES = [
  { id: 'loads', label: 'Cargas' },
  { id: 'users', label: 'Usuarios' },
];

const TYPE_LABELS = {
  loads: 'Cargas',
  users: 'Usuarios',
};

function UsageExamplePanel({ webhook, baseUrl }) {
  const [tab, setTab] = useState(0);
  const { showSuccess } = useNotification();

  const endpointUrl = `${baseUrl}/api/webhook/${webhook.slug}`;

  const fields = webhook.skill_fields || [];
  const numberFields = fields.filter(f => f.number_mapping);
  const extraFields = fields.filter(f => !f.number_mapping);

  // Build example extradata
  const exampleExtra = {};
  extraFields.forEach(f => {
    if (f.type === 'numero entero' || f.type === 'numero doble') {
      exampleExtra[f.name] = 0;
    } else if (f.type === 'Fecha' || f.type === 'fecha') {
      exampleExtra[f.name] = '2026-01-15';
    } else {
      exampleExtra[f.name] = `valor_${f.name}`;
    }
  });

  const singleExample = { token: 'TU_API_TOKEN_AQUI', number: '3201234567' };
  if (numberFields.length > 1) {
    numberFields.slice(1).forEach((f, i) => {
      singleExample[`number${i + 2}`] = `320987654${i + 1}`;
    });
  }
  if (extraFields.length > 0) {
    singleExample.extradata = exampleExtra;
  }

  const batchExample = {
    token: 'TU_API_TOKEN_AQUI',
    records: [
      (() => {
        const rec = { number: '3201234567' };
        if (extraFields.length > 0) {
          rec.extradata = { ...exampleExtra };
        }
        return rec;
      })(),
      (() => {
        const rec = { number: '3209876543' };
        if (extraFields.length > 0) {
          const ext = {};
          extraFields.forEach(f => { ext[f.name] = `otro_${f.name}`; });
          rec.extradata = ext;
        }
        return rec;
      })()
    ]
  };

  const minimalExample = { token: 'TU_API_TOKEN_AQUI', number: '3201234567' };

  const curlSingle = `curl -X POST "${endpointUrl}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(singleExample, null, 2)}'`;

  const curlBatch = `curl -X POST "${endpointUrl}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(batchExample, null, 2)}'`;

  const curlMinimal = `curl -X POST "${endpointUrl}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(minimalExample)}'`;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    showSuccess('Copiado al portapapeles');
  };

  const responseExample = JSON.stringify({
    success: true,
    data: { inserted: 1, errors: [], elapsed_ms: 45 }
  }, null, 2);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">URL del Webhook</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            POST {endpointUrl}
          </Typography>
          <IconButton size="small" onClick={() => copy(endpointUrl)}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Box>
      </Alert>

      <Alert severity="warning" sx={{ mb: 2 }}>
        El token de autenticacion debe enviarse en el campo <b>"token"</b> del body JSON,
        o como header <b>Authorization: Bearer TU_TOKEN</b>.
        Obten tu token en la seccion <b>API Tokens</b>.
      </Alert>

      {webhook.type === 'loads' && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Skill: {webhook.skill_name} | Carga: {webhook.load_name || `#${webhook.load_id}`}
          </Typography>

          {fields.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Campo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Mapeo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>En JSON</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map(f => (
                    <TableRow key={f.name}>
                      <TableCell>{f.name}</TableCell>
                      <TableCell>{f.type}</TableCell>
                      <TableCell>
                        {f.number_mapping ? (
                          <Chip label={f.number_mapping} size="small" color="primary" variant="outlined" />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {f.number_mapping
                            ? (f.number_mapping === 'number1' ? '"number"' : `"${f.number_mapping}"`)
                            : `extradata.${f.name}`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Registro Simple" />
            <Tab label="Envio Masivo" />
            <Tab label="Solo Numero" />
            <Tab label="Respuesta" />
          </Tabs>

          {tab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">cURL - Registro con datos del cliente</Typography>
                <Button size="small" startIcon={<CopyIcon />} onClick={() => copy(curlSingle)}>Copiar</Button>
              </Box>
              <Paper sx={{ p: 2, backgroundColor: '#1e1e1e', overflow: 'auto' }}>
                <pre style={{ color: '#d4d4d4', margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                  {curlSingle}
                </pre>
              </Paper>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">cURL - Envio de multiples registros (max 1000)</Typography>
                <Button size="small" startIcon={<CopyIcon />} onClick={() => copy(curlBatch)}>Copiar</Button>
              </Box>
              <Paper sx={{ p: 2, backgroundColor: '#1e1e1e', overflow: 'auto' }}>
                <pre style={{ color: '#d4d4d4', margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                  {curlBatch}
                </pre>
              </Paper>
            </Box>
          )}

          {tab === 2 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">cURL - Solo numero de telefono (sin datos extra)</Typography>
                <Button size="small" startIcon={<CopyIcon />} onClick={() => copy(curlMinimal)}>Copiar</Button>
              </Box>
              <Paper sx={{ p: 2, backgroundColor: '#1e1e1e', overflow: 'auto' }}>
                <pre style={{ color: '#d4d4d4', margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                  {curlMinimal}
                </pre>
              </Paper>
            </Box>
          )}

          {tab === 3 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Respuesta exitosa</Typography>
              <Paper sx={{ p: 2, backgroundColor: '#1e1e1e', overflow: 'auto' }}>
                <pre style={{ color: '#a5d6a7', margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                  {responseExample}
                </pre>
              </Paper>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Respuesta con errores parciales</Typography>
              <Paper sx={{ p: 2, backgroundColor: '#1e1e1e', overflow: 'auto' }}>
                <pre style={{ color: '#ffcc80', margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
{JSON.stringify({
  success: true,
  data: {
    inserted: 1,
    errors: [{ index: 1, number: 'abc', error: 'Se requiere al menos un numero de telefono valido' }],
    elapsed_ms: 52
  }
}, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </>
      )}

      {webhook.type !== 'loads' && (
        <Alert severity="info">
          Los ejemplos de uso para el tipo "{TYPE_LABELS[webhook.type] || webhook.type}" estaran disponibles proximamente.
        </Alert>
      )}
    </Box>
  );
}

const DEFAULT_FORM = { name: '', type: 'loads', skill_id: '', load_id: '' };

export default function WebhooksPage() {
  const { webhooks, skills, loads } = useContext(ApiContext);
  const { showSuccess, showError } = useNotification();
  const { canAction } = usePermissions();

  const [items, setItems] = useState([]);
  const [skillsList, setSkillsList] = useState([]);
  const [loadsList, setLoadsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [viewWebhook, setViewWebhook] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: null, name: '', skill_id: '', load_id: '' });
  const [editLoadsList, setEditLoadsList] = useState([]);

  const baseUrl = useMemo(() => window.location.origin, []);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const res = await webhooks.getAll();
      setItems(res?.data?.webhooks || []);
    } catch (e) {
      showError(`Error cargando webhooks: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [webhooks, showError]);

  const loadSkills = useCallback(async () => {
    try {
      const res = await skills.getAll();
      setSkillsList(res?.data?.skills || []);
    } catch (e) {
      console.error('Error loading skills:', e);
    }
  }, [skills]);

  useEffect(() => { loadAll(); loadSkills(); }, [loadAll, loadSkills]);

  // Cargar las cargas del skill seleccionado
  const loadSkillLoads = useCallback(async (skillId) => {
    if (!skillId) {
      setLoadsList([]);
      return;
    }
    try {
      const res = await webhooks.getLoads(skillId);
      setLoadsList(res?.data?.loads || []);
    } catch (e) {
      console.error('Error loading skill loads:', e);
      setLoadsList([]);
    }
  }, [webhooks]);

  useEffect(() => {
    if (form.type === 'loads' && form.skill_id) {
      loadSkillLoads(form.skill_id);
    } else {
      setLoadsList([]);
    }
  }, [form.skill_id, form.type, loadSkillLoads]);

  const handleCreate = async () => {
    try {
      if (!form.name.trim()) {
        showError('El nombre es requerido');
        return;
      }
      if (!form.type) {
        showError('El tipo es requerido');
        return;
      }
      if (form.type === 'loads' && !form.skill_id) {
        showError('Selecciona un skill');
        return;
      }
      if (form.type === 'loads' && !form.load_id) {
        showError('Selecciona una carga');
        return;
      }
      await webhooks.create(form);
      showSuccess('Webhook creado correctamente');
      setOpen(false);
      setForm({ ...DEFAULT_FORM });
      setLoadsList([]);
      await loadAll();
    } catch (e) {
      showError(`Error creando webhook: ${e.message}`);
    }
  };

  const handleToggle = async (item) => {
    try {
      await webhooks.update(item.id, { active: !item.active });
      showSuccess(`Webhook ${item.active ? 'desactivado' : 'activado'}`);
      await loadAll();
    } catch (e) {
      showError(`Error: ${e.message}`);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminar webhook "${item.name}"?`)) return;
    try {
      await webhooks.delete(item.id);
      showSuccess('Webhook eliminado');
      await loadAll();
    } catch (e) {
      showError(`Error: ${e.message}`);
    }
  };

  const handleEditOpen = async (item) => {
    setEditForm({
      id: item.id,
      name: item.name,
      skill_id: item.skill_id || '',
      load_id: item.load_id || ''
    });
    if (item.type === 'loads' && item.skill_id) {
      try {
        const res = await webhooks.getLoads(item.skill_id);
        setEditLoadsList(res?.data?.loads || []);
      } catch (e) {
        setEditLoadsList([]);
      }
    } else {
      setEditLoadsList([]);
    }
    setEditOpen(true);
  };

  const handleEditSkillChange = async (skillId) => {
    setEditForm(prev => ({ ...prev, skill_id: skillId, load_id: '' }));
    if (skillId) {
      try {
        const res = await webhooks.getLoads(skillId);
        setEditLoadsList(res?.data?.loads || []);
      } catch (e) {
        setEditLoadsList([]);
      }
    } else {
      setEditLoadsList([]);
    }
  };

  const handleEditSave = async () => {
    try {
      if (!editForm.name.trim()) {
        showError('El nombre es requerido');
        return;
      }
      await webhooks.update(editForm.id, {
        name: editForm.name,
        skill_id: editForm.skill_id || null,
        load_id: editForm.load_id || null
      });
      showSuccess('Webhook actualizado');
      setEditOpen(false);
      await loadAll();
    } catch (e) {
      showError(`Error: ${e.message}`);
    }
  };

  const handleViewUsage = async (item) => {
    try {
      const res = await webhooks.getById(item.id);
      setViewWebhook(res?.data?.webhook || null);
      setOpenView(true);
    } catch (e) {
      showError(`Error: ${e.message}`);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccess('Copiado al portapapeles');
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-ES') : '-';

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">Webhooks</Typography>
            <Box>
              <Button startIcon={<RefreshIcon />} onClick={loadAll} sx={{ mr: 1 }} disabled={loading}>
                Actualizar
              </Button>
              {canAction('create_webhook') && <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
                setForm({ ...DEFAULT_FORM });
                setLoadsList([]);
                setOpen(true);
              }}>
                Nuevo Webhook
              </Button>}
            </Box>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <Paper variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Skill / Carga</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Registros</TableCell>
                  <TableCell>Requests</TableCell>
                  <TableCell>Ultimo uso</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2">{item.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={TYPE_LABELS[item.type] || item.type}
                        size="small"
                        variant="outlined"
                        color="info"
                      />
                    </TableCell>
                    <TableCell>
                      {item.skill_name && (
                        <Typography variant="body2">{item.skill_name}</Typography>
                      )}
                      {item.load_name && (
                        <Typography variant="caption" color="text.secondary">{item.load_name}</Typography>
                      )}
                      {!item.skill_name && !item.load_name && '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          /api/webhook/{item.slug}
                        </Typography>
                        <Tooltip title="Copiar URL completa">
                          <IconButton size="small" onClick={() => copyToClipboard(`${baseUrl}/api/webhook/${item.slug}`)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.active ? 'Activo' : 'Inactivo'}
                        color={item.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.total_records || 0}</Typography>
                      {(item.total_errors || 0) > 0 && (
                        <Typography variant="caption" color="error">{item.total_errors} errores</Typography>
                      )}
                    </TableCell>
                    <TableCell>{item.total_requests || 0}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(item.last_used_at)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={item.active ? 'Desactivar' : 'Activar'}>
                        <Switch
                          size="small"
                          checked={item.active}
                          onChange={() => handleToggle(item)}
                        />
                      </Tooltip>
                      {canAction('edit_webhook') && <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEditOpen(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>}
                      <Tooltip title="Ver uso y ejemplos">
                        <IconButton size="small" color="primary" onClick={() => handleViewUsage(item)}>
                          <CodeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canAction('delete_webhook') && <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>}
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No hay webhooks creados</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>

      {/* Dialog Crear Webhook */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo Webhook</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre"
            fullWidth
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            placeholder="Ej: crm-leads-ingreso"
            helperText="Se usara como parte de la URL del webhook"
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={form.type}
              label="Tipo"
              onChange={(e) => setForm({ ...form, type: e.target.value, skill_id: '', load_id: '' })}
            >
              {WEBHOOK_TYPES.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {form.type === 'loads' && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Skill</InputLabel>
                <Select
                  value={form.skill_id}
                  label="Skill"
                  onChange={(e) => setForm({ ...form, skill_id: e.target.value, load_id: '' })}
                >
                  {skillsList.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.skillname}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Carga destino</InputLabel>
                <Select
                  value={form.load_id}
                  label="Carga destino"
                  onChange={(e) => setForm({ ...form, load_id: e.target.value })}
                  disabled={!form.skill_id || loadsList.length === 0}
                >
                  {loadsList.map(l => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.name} {l.total_records ? `(${l.total_records} reg.)` : ''}
                    </MenuItem>
                  ))}
                </Select>
                {form.skill_id && loadsList.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    No hay cargas para este skill. Crea una primero en el modulo de Cargas.
                  </Typography>
                )}
              </FormControl>
            </>
          )}

          {form.type === 'users' && (
            <Alert severity="info">
              El tipo "Usuarios" estara disponible proximamente.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Editar Webhook */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Webhook</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre"
            fullWidth
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Skill</InputLabel>
            <Select
              value={editForm.skill_id}
              label="Skill"
              onChange={(e) => handleEditSkillChange(e.target.value)}
            >
              {skillsList.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.skillname}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Carga destino</InputLabel>
            <Select
              value={editForm.load_id}
              label="Carga destino"
              onChange={(e) => setEditForm({ ...editForm, load_id: e.target.value })}
              disabled={!editForm.skill_id || editLoadsList.length === 0}
            >
              {editLoadsList.map(l => (
                <MenuItem key={l.id} value={l.id}>
                  {l.name} {l.total_records ? `(${l.total_records} reg.)` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEditSave}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Ver Uso y Ejemplos */}
      <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Uso del Webhook: {viewWebhook?.name}
        </DialogTitle>
        <DialogContent dividers>
          {viewWebhook && (
            <UsageExamplePanel webhook={viewWebhook} baseUrl={baseUrl} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenView(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
